import { first, get } from 'lodash';
import React from 'react';
import I18n from 'i18n-js';
import { ChildProps, MutationFn } from 'react-apollo';
import { convertCurrency } from '@reverbdotcom/commons/src/convert_currency';
import {
  connectEditOfferFormQuery,
  connectUpdateNegotiationMutation,
} from '@reverbdotcom/commons/src/offers/offer_operations';
import NegotiationSummary from './negotiation_summary';
import UpdateOfferFormFields from './update_offer_form_fields';
import URLHelpers from '../shared/url_helpers';
import * as elog from '@reverbdotcom/commons/src/elog';

import {
  CommonsOffersEditOfferForm,
  CommonsOffersUpdateOfferForm,
  Input_core_apimessages_UpdateNegotiationRequest,
  Input_core_apimessages_OfferItemRequest,
  core_apimessages_NegotiationParty as NegotiationParty,
  core_apimessages_NegotiationAction as NegotiationAction,
} from '../../schemas/rql';

import {
  IUserContext,
  withUserContext,
} from '@reverbdotcom/commons/src/components/user_context_provider';
import ModalDialog from '@reverbdotcom/commons/src/components/modal_dialog';
import { offerFormReducer, emptyOfferFormState } from '@reverbdotcom/commons/src/offers/create_offer_modal';
import { OfferListingSidebar } from '@reverbdotcom/commons/src/offers/offer_listing_sidebar';

import {
  lastOffer as getLastOffer,
  lastOfferPrices as getLastOfferPrices,
  lastOfferPricesFor,
  activeNegotiation,
  offerShippingPriceAmount,
  offerQuantity,
  userCurrency as getUserCurrency,
  originalCurrency,
} from './negotiation_getters';
import { RCAlertBox } from '@reverbdotcom/cadence/components';
import { connect } from '@reverbdotcom/commons/src/connect';

interface IExternalProps {
  listingId: string;
  isSeller: boolean;
  negotiationId: string;
  onSuccess: (requestingParty: NegotiationParty) => void;
  isDialogOpen?: boolean;
  onRequestDialogClose?: () => void;
  negotiationAction: NegotiationAction;
}

interface ApolloProps {
  updateNegotiation: MutationFn<CommonsOffersUpdateOfferForm.Negotiation, CommonsOffersUpdateOfferForm.Variables>;
}

type IProps = ChildProps<IExternalProps, CommonsOffersEditOfferForm.Query> & ApolloProps & IUserContext;

const TRACKING_COMPONENT = 'UpdateOfferModal';

interface BaseOffer {
  price: string;
  shippingPrice: string;
  quantity: number;
  message: string;
}

function OfferSidebar({ listing, loading, isSeller }) {
  return (
    <OfferListingSidebar
      listing={listing}
      isLoading={loading}
      isSeller={isSeller}
      listingUrlGenerator={URLHelpers.listingPagePath}
    />
  );
}

function ErrorDisplay() {
  return (
    <RCAlertBox type="error">
      <p className="mb-0">
        {I18n.t('commons.offers.error')}
      </p>
    </RCAlertBox>
  );
}

function LoadingDisplay() {
  return (
    <div className="offers-form offers-form--loading">
      <div className="offers-form-placeholder" />
      <div className="offers-form-placeholder" />
      <div className="offers-form-placeholder" />
    </div>
  );
}

export function UpdateOfferModalDialog({
  isDialogOpen,
  onRequestDialogClose,
  negotiationAction,
  isSeller,
  updateNegotiation,
  onSuccess,
  listingId,
  negotiationId,
  data,
  user,
}: IProps) {
  const [formState, dispatch] = React.useReducer(offerFormReducer, emptyOfferFormState);
  const [isFormSaving, setIsFormSaving] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');

  const { loading, error, listing } = data || {};

  const modalHeaderTitle = I18n.t(`discovery.offers.headerTitle.${negotiationAction.toLocaleLowerCase()}`);
  const negotiation = activeNegotiation(data?.me);
  const lastOffer = getLastOffer(negotiation);
  const userCurrency = getUserCurrency(data, user);
  const exchangeRates = data?.exchangeRates?.rates || [];
  const party = isSeller ? NegotiationParty.SELLER : NegotiationParty.BUYER;
  const lastOfferPrices = getLastOfferPrices(data?.me);
  const originalLastOfferPrices = lastOfferPricesFor(NegotiationParty.SELLER, negotiation);
  const isCounterOffer = negotiationAction === NegotiationAction.COUNTER;
  const closeDialog = () => {
    if (onRequestDialogClose) {
      onRequestDialogClose();
    }
  };

  const missingExchangeRates = !exchangeRates.length &&
    !isSeller &&
    (userCurrency !== originalCurrency(data));
  const showError = error || (!negotiation && !loading) || missingExchangeRates;
  const submitDisabled = isFormSaving || (isCounterOffer && !formState.price);

  function inputOffer(): BaseOffer {
    const shippingPrice = offerShippingPriceAmount(
      formState.shippingPrice || undefined, // the default shipping price behavior kicks in when the value is undefined, not the empty string
      data?.me,
    );
    const quantity = offerQuantity(lastOffer, formState.quantity);

    return {
      quantity,
      shippingPrice,
      price: formState.price,
      message: formState.message,
    };
  }

  function saveButtonText() {
    switch (negotiationAction) {
      case NegotiationAction.ACCEPT:
        return I18n.t('discovery.offers.headerTitle.accept');
      case NegotiationAction.REJECT:
        return I18n.t('discovery.offers.headerTitle.reject');
      case NegotiationAction.COUNTER:
      default:
        return I18n.t('discovery.offers.submitButton.counter.label');
    }
  }

  function existingOffer(): BaseOffer {
    return {
      price: lastOffer.prices.price.display.amount,
      shippingPrice: lastOffer.prices.shippingPrice.display.amount,
      quantity: first(lastOffer.offerItems).quantity,
      message: formState.message,
    };
  }

  function offerToSubmitOnUpdate(): Input_core_apimessages_UpdateNegotiationRequest {
    const baseOffer = isCounterOffer ? inputOffer() : existingOffer();

    const shopCurrencyOfferPrice = convertCurrency(
      userCurrency,
      originalCurrency(data),
      exchangeRates,
      baseOffer.price,
    );
    const shopCurrencyOfferShippingPrice = convertCurrency(
      userCurrency,
      originalCurrency(data),
      exchangeRates,
      baseOffer.shippingPrice,
    );

    return {
      action: negotiationAction,
      id: negotiation.id,
      message: formState.message,
      offerItems: [
        {
          listingId: data.listing.id,
          quantity: baseOffer.quantity,
          price: shopCurrencyOfferPrice,
          shippingPrice: shopCurrencyOfferShippingPrice,
        },
      ],
    };
  }

  function trackingContext() {
    return {
      listingId: listingId,
      negotiationId: negotiationId,
      shopCurrency: listing.shop.currency,
      userCurrency: userCurrency,
    };
  }

  async function doNegotiationUpdate(): Promise<Input_core_apimessages_OfferItemRequest> {
    const input = offerToSubmitOnUpdate();
    await updateNegotiation({ variables: { input } });
    return first(input.offerItems);
  }

  async function handleSubmit() {
    try {
      setIsFormSaving(true);
      setErrorMessage('');

      await doNegotiationUpdate();

      setIsFormSaving(false);

      onSuccess(party);
      closeDialog();
    } catch (errors) {
      setIsFormSaving(false);
      setErrorMessage(I18n.t('discovery.offers.updateOfferSubmitError'));

      const { message: graphQLErrorMessage } = first(get(errors, 'graphQLErrors', [{ message: '' }]));
      elog.error(
        TRACKING_COMPONENT,
        {
          ...trackingContext(),
          errors,
        },
        new Error(graphQLErrorMessage),
      );
    }
  }

  if (!data) {
    return null;
  }

  return (
    <ModalDialog
      isOpen={isDialogOpen}
      onRequestClose={closeDialog}
      headerTitle={modalHeaderTitle}
      sidebarContent={(
        <OfferSidebar
          listing={listing}
          loading={loading}
          isSeller={isSeller}
        />
      )}
      onSubmit={handleSubmit}
      saveButtonText={saveButtonText()}
      isDisabled={submitDisabled}
    >
      {showError && <ErrorDisplay/>}

      {loading && !listing && <LoadingDisplay/>}

      {!loading && !error && listing && negotiation && (
        <>
          <div className="offers-form__header">
            <NegotiationSummary
              prices={lastOfferPrices}
              originalPrices={originalLastOfferPrices}
              listing={listing}
              negotiation={negotiation}
              party={party}
            />
          </div>
          <UpdateOfferFormFields
            error={errorMessage}
            listing={listing}
            negotiation={negotiation}
            currency={userCurrency}
            exchangeRates={exchangeRates}
            offer={inputOffer()}
            onOfferChange={dispatch}
            action={negotiationAction}
            party={party}
            modalDialogVersion={true}
          />
        </>
      )}
    </ModalDialog>
  );
}

export default connect<IExternalProps>([
  connectEditOfferFormQuery,
  connectUpdateNegotiationMutation,
  withUserContext,
])(UpdateOfferModalDialog);
